<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { USERS_MODULE } from "@/state/modules/users";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { usersMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page: { title: i18n.t("label.updateX", [i18n.t("module.user")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.updateX", [i18n.t("module.user")]),
      items: [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.user"), href: "/users" },
        { text: i18n.t("label.updateX", [i18n.t("module.user")]), active: true },
      ],
      form: { name: "", email: "", roleUuid: "", agentCode: "" },
      currentLocationCode: "",
      submitting: false,
      selectedRole: null,
      selectedAgent: null,
      isLocked: false,
    };
  },
  validations: {
    form: {
      name: { required },
      email: { required, email },
      roleUuid: { required },
    },
  },
  computed: {
    ...mapState(USERS_MODULE, ["details"]),
    ...mapState(LOOKUP_MODULE, ["roleDdl", "unlinkedAgentDdl"]),
  },
  async created() {
    const uuid = this.$route.params.id;
    await Promise.all([
      this.lookupRole(),
      this.lookupUnlinkedAgent({ excludeUserUuid: uuid }),
    ]);
    const result = await this.getUsers({ id: uuid });
    const d = result?.data || {};
    this.form = {
      name: d.name || "",
      email: d.email || "",
      roleUuid: (d.roles && d.roles[0] && (d.roles[0].uuid || d.roles[0].value)) || "",
      agentCode: d.agent_code || "",
    };
    this.currentLocationCode = d.location_code || "";
    this.isLocked = !!d.is_locked;
    this.selectedRole = (this.roleDdl || []).find((r) => r.value === this.form.roleUuid) || null;
    this.selectedAgent = (this.unlinkedAgentDdl || []).find(
      (a) => a.agent_code === this.form.agentCode
    ) || null;
  },
  methods: {
    ...usersMethods,
    ...lookupMethods,
    onRoleChange(r) {
      this.selectedRole = r;
      this.form.roleUuid = r ? r.value : "";
    },
    onAgentChange(a) {
      this.selectedAgent = a;
      this.form.agentCode = a ? a.agent_code : "";
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.submitting = true;
      try {
        const result = await this.updateUsers({
          id: this.$route.params.id,
          data: this.form,
          filter: {},
        });
        // Store action returns undefined on error (caught + alerted internally),
        // so only navigate when we got a successful response.
        if (result) this.$router.push({ name: "Users-listing" });
      } finally {
        this.submitting = false;
      }
    },
    async onUnlock() {
      const ok = await this.$bvModal.msgBoxConfirm(
        "Unlock this user's account?",
        { okTitle: this.$t("label.unlock") || "Unlock", cancelTitle: this.$t("label.cancel"), centered: true }
      );
      if (!ok) return;
      await this.unlockUsers({ id: this.$route.params.id, filter: {} });
      this.isLocked = false;
    },
    cancel() {
      this.$router.push({ name: "Users-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-alert v-if="isLocked" variant="warning" show class="d-flex justify-content-between align-items-center">
              <span><i class="mdi mdi-lock-outline mr-1"></i> This account is locked.</span>
              <b-button size="sm" variant="warning" @click="onUnlock">
                <i class="mdi mdi-lock-open-outline mr-1"></i> {{ $t('label.unlock') }}
              </b-button>
            </b-alert>
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name')">
                    <b-form-input v-model="form.name" :state="$v.form.name.$dirty ? !$v.form.name.$error : null" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.email')">
                    <b-form-input type="email" v-model="form.email" :state="$v.form.email.$dirty ? !$v.form.email.$error : null" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.role')">
                    <multiselect
                      :value="selectedRole"
                      :options="roleDdl"
                      track-by="value"
                      label="name"
                      :show-labels="false"
                      :placeholder="'Select role'"
                      @input="onRoleChange"
                    />
                    <small v-if="$v.form.roleUuid.$dirty && $v.form.roleUuid.$error" class="text-danger">
                      {{ $t('label.role') }} is required
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.agentCode')">
                    <multiselect
                      :value="selectedAgent"
                      :options="unlinkedAgentDdl"
                      track-by="agent_code"
                      :custom-label="(a) => `${a.agent_code} — ${a.agent_name || ''}`"
                      :show-labels="false"
                      :allow-empty="true"
                      :placeholder="'Select agent (only unlinked shown)'"
                      @input="onAgentChange"
                    />
                    <small class="text-muted">The current user's existing link is included; other linked agents are hidden.</small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="AutoCount Location">
                    <div v-if="currentLocationCode">
                      <b-badge variant="info" class="font-monospace">{{ currentLocationCode }}</b-badge>
                      <small class="text-muted ml-2">Auto-generated when this user was linked to an agent.</small>
                    </div>
                    <div v-else class="text-muted small">
                      <i class="mdi mdi-information-outline mr-1"></i>
                      Will be auto-created as <code>AGT&lt;user_id&gt;</code> when you link an agent and save.
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="text-right">
                  <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                  <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
                </b-col>
              </b-row>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
